import { useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'

interface Props {
  /** The thinking/reasoning content */
  content: string
  /** Whether the model is still reasoning (streaming) */
  isStreaming?: boolean
  /** Whether this is a Secure (Cocoon TEE) model */
  isSecure?: boolean
  /** Duration of thinking in seconds (shown after completion) */
  duration?: number
}

export default function ThinkingBlock({ content, isStreaming, isSecure, duration }: Props) {
  // Start expanded during streaming, collapsed when complete
  const [expanded, setExpanded] = useState(!!isStreaming)

  const accentColor = isSecure ? '#A062FF' : 'var(--tg-theme-accent-text-color)'

  const headerText = isStreaming
    ? 'Reasoning...'
    : duration
      ? `Thought for ${duration.toFixed(1)}s`
      : 'Thought process'

  return (
    <div
      className="rounded-[10px] mb-sp-2"
      style={{
        border: '1px solid var(--tg-theme-secondary-bg-color)',
        backgroundColor: 'var(--tg-theme-bg-color)',
      }}
    >
      {/* Header — always visible, clickable */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-sp-3 py-sp-2 active:opacity-70 transition-opacity"
      >
        <div className="flex items-center gap-sp-1">
          <span className="text-[10px]" style={{ color: accentColor }}>
            ◆
          </span>
          <span
            className="text-[12px] font-medium"
            style={{ color: 'var(--tg-theme-hint-color)' }}
          >
            {headerText}
          </span>
        </div>
        {expanded ? (
          <ChevronUp size={14} style={{ color: 'var(--tg-theme-hint-color)', opacity: 0.5 }} />
        ) : (
          <ChevronDown size={14} style={{ color: 'var(--tg-theme-hint-color)', opacity: 0.5 }} />
        )}
      </button>

      {/* Content — collapsible */}
      {expanded && content && (
        <div
          className="px-sp-3 pb-sp-2 text-[13px] leading-relaxed overflow-y-auto"
          style={{
            color: 'var(--tg-theme-hint-color)',
            borderTop: '1px solid var(--tg-theme-secondary-bg-color)',
            paddingTop: 'var(--space-2)',
            maxHeight: '200px',
          }}
        >
          <p className={`whitespace-pre-wrap ${isStreaming ? 'streaming-cursor' : ''}`}>
            {content}
          </p>
        </div>
      )}
    </div>
  )
}
